import { useCallback, useEffect, useRef, useState } from "react";
import { reviewTemplate, type ReviewItem } from "../../shared/review-templates";
import type { ApprovalDTO, ReviewContextDTO, ReviewDecision } from "../../shared/types";
import { useUser } from "../App";
import { AgentAvatar } from "../components/AgentAvatar";
import { Icon } from "../components/Icon";
import { IconButton } from "../components/IconButton";
import { Overlay } from "../components/Overlay";
import { Tabs, useTabParam } from "../components/Tabs";
import { TextLink } from "../components/TextLink";
import { Badge, Button, ErrorText, shortDate, timeAgo } from "../components/ui";
import { ApiError, get, post } from "../lib/api";
import { canCheckSend, canDecideRequest, canSettleRelease, releaseState, releaseText, verdictWord, waitingForText } from "../lib/approval-text";
import { useLocation } from "../lib/router";
import { realtime } from "../lib/ws";
import { AllDone, NoteSheet, ReleaseBar, thumbCls, VersionHistory } from "./ReviewOutreachBars";
import { MessageFrame, Parties, StateStrip, text, WhyNow } from "./ReviewOutreachMessage";

/**
 * The review page for `outreach-message`: one message per screen, with the picture a person needs to decide above
 * it and the three verdicts under the thumb. Top to bottom: the company, the contact, where we are with them, why
 * now, then the message exactly as it would go out. The live picture comes from SalesOS through
 * `GET /api/review/:id/context`, which also carries the item's other versions; neither can hold up a verdict.
 *
 * A verdict is the row's verdict — the same route the Requests page posts to — then the card leaves and the next
 * one slides in. Approving is the send (D14): the platform logs the message in SalesOS Outreach right then. One the
 * tool refused stays on screen, approved and not sent, for its addressee to send again or withdraw.
 */

const TEMPLATE = "outreach-message";

export function OutreachReviewPage({ onClose }: { admin?: boolean; onClose: () => void }) {
  const user = useUser();
  const template = reviewTemplate(TEMPLATE)!;
  const { search } = useLocation();
  const wanted = search.get("item");

  // Its own parameter, so the Requests page drawn beneath keeps its `?view=`.
  const [who, setWho] = useTabParam(["mine", "all"] as const, "who");
  const [queue, setQueue] = useState<ApprovalDTO[] | null>(null);
  const [index, setIndex] = useState(0);
  const [sheet, setSheet] = useState<"revise" | "reject" | "withdraw" | null>(null);
  const [exiting, setExiting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [more, setMore] = useState(0);
  // What the row is wrapped in: its other versions, and the live picture from SalesOS.
  const [context, setContext] = useState<ReviewContextDTO | null>(null);
  // An older version being read instead of the one waiting, and the list of them all.
  const [showingId, setShowingId] = useState<string | null>(null);
  const [history, setHistory] = useState(false);
  // Paging back and forth must not re-read anything: what we have already been told about a row is kept.
  const known = useRef(new Map<string, ReviewContextDTO>());
  // The deep link lands on its own item once; paging from there is the person's own.
  const landed = useRef(false);
  // What we decided here ourselves: its own verdict comes back over the socket, and must not move the queue twice.
  const decidedHere = useRef(new Set<string>());

  /**
   * A link to one message that is not in this person's queue: addressed to someone else, or no longer waiting. One
   * that is still waiting and readable is drawn first, its verdicts off, with who decides it.
   */
  const landOutside = useCallback(async (rows: ApprovalDTO[], id: string): Promise<number> => {
    const row = await get<ApprovalDTO>(`/api/approvals/${id}`).catch((err) => (err instanceof ApiError && err.status === 403 ? "theirs" : null));
    if (row === "theirs") setNotice("That message is addressed to someone else.");
    else if (row?.status !== "pending" || row.template !== TEMPLATE) setNotice("That message is no longer waiting for a verdict.");
    else if (!canDecideRequest(row, user)) {
      // The bar under the thumb says who decides it; with nothing of it readable, this line is all there is.
      if (Object.keys(row.payload ?? {}).length === 0) {
        setNotice(`That message is addressed to ${waitingForText(row)}.`);
        return -1;
      }
      rows.unshift(row);
      return 0;
    }
    return -1;
  }, [user]);

  const load = useCallback(
    async (opts: { keepPlace?: boolean } = {}) => {
      try {
        const rows = await get<ApprovalDTO[]>(`/api/review/pending/${TEMPLATE}?view=${who}`);
        setMore(0);
        setError(null);
        if (opts.keepPlace) return setQueue(rows);
        let at = !landed.current && wanted ? rows.findIndex((r) => r.id === wanted) : -1;
        if (!landed.current && wanted && at < 0) at = await landOutside(rows, wanted);
        landed.current = true;
        setQueue(rows);
        setIndex(at >= 0 ? at : 0);
      } catch (err) {
        setError((err as Error).message);
      }
    },
    [who, wanted, landOutside],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const item = queue?.[index] ?? null;
  const done = Boolean(queue) && (queue!.length === 0 || index >= queue!.length);
  const canDecide = Boolean(item) && canDecideRequest(item!, user);

  // The picture behind the row under the thumb, and the row after it, so paging feels instant. A tool that is slow
  // or down never holds up the message: the page simply says so where the numbers would be.
  useEffect(() => {
    if (!item) return;
    setShowingId(null);
    setHistory(false);
    setContext(known.current.get(item.id) ?? null);
    let live = true;
    const fetchOne = async (id: string) => {
      if (known.current.has(id)) return known.current.get(id)!;
      const ctx = await get<ReviewContextDTO>(`/api/review/${id}/context`);
      known.current.set(id, ctx);
      return ctx;
    };
    void fetchOne(item.id)
      .then((ctx) => {
        if (live) setContext(ctx);
        const next = queue?.[index + 1];
        if (next) void fetchOne(next.id).catch(() => {});
      })
      .catch(() => {
        if (live) setContext({ versions: [], salesos: { available: false, unavailable: "the platform could not read it", company: null, contact: null, state: null } });
      });
    return () => {
      live = false;
    };
  }, [item?.id, index, queue]);

  /** The card leaves, the next slides in. */
  const advance = useCallback((by = 1) => {
    setExiting(true);
    setTimeout(() => {
      setExiting(false);
      setIndex((i) => Math.max(0, i + by));
    }, 200);
  }, []);

  /** After a verdict or a send: an approved message that did not go stays on screen with what to do; anything else leaves. */
  const settle = (updated: ApprovalDTO) => {
    decidedHere.current.add(updated.id);
    const state = releaseState(updated);
    if (state === "unsent" || state === "uncertain") return setQueue((q) => q && q.map((r, i) => (i === index ? updated : r)));
    advance();
  };

  // Decided somewhere else meanwhile — the Requests page, another device: say so and move on.
  useEffect(() => {
    if (!item?.conversationId) return;
    return realtime.subscribe(`conversation:${item.conversationId}`, (event) => {
      if (event.type !== "approval.updated") return;
      const updated = event.payload as ApprovalDTO;
      if (updated.id !== item.id || updated.status === "pending" || decidedHere.current.has(updated.id)) return;
      setNotice(`Already decided by ${updated.decidedByName ?? "someone"}.`);
      advance();
    });
  }, [item?.id, item?.conversationId, advance]);

  // Whatever arrives while the queue is being worked through is counted, never slipped in under the thumb.
  useEffect(() => {
    if (!queue) return;
    const timer = setInterval(async () => {
      const rows = await get<ApprovalDTO[]>(`/api/review/pending/${TEMPLATE}?view=${who}`).catch(() => null);
      if (!rows) return;
      const seen = new Set(queue.map((r) => r.id));
      setMore(rows.filter((r) => !seen.has(r.id)).length);
    }, 15_000);
    return () => clearInterval(timer);
  }, [queue, who]);

  const decide = async (decision: ReviewDecision, note?: string) => {
    if (!item) return;
    setBusy(true);
    setNotice(null);
    try {
      decidedHere.current.add(item.id);
      const updated = await post<ApprovalDTO>(`/api/approvals/${item.id}/decide`, { decision, note: note || undefined });
      setSheet(null);
      settle(updated);
    } catch (err) {
      // Someone else got there first: show where it stands and carry on down the queue.
      if (err instanceof ApiError && err.status === 409) {
        const fresh = await get<ApprovalDTO>(`/api/approvals/${item.id}`).catch(() => null);
        setNotice(`Already decided by ${fresh?.decidedByName ?? "someone"}.`);
        setSheet(null);
        advance();
        return;
      }
      // It did not land, so the row is still somebody's to decide: a verdict given elsewhere must still show here.
      decidedHere.current.delete(item.id);
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const verdict = (decision: ReviewDecision, note?: string) =>
    decide(decision, note).catch((err) => setError((err as Error).message));

  /** Send again or withdraw an approved message the tool refused. A 409 means it moved meanwhile: show where it stands. */
  const release = async (path: "send" | "withdraw" | "checked", reason?: string) => {
    if (!item) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await post<ApprovalDTO>(`/api/approvals/${item.id}/${path}`, path === "withdraw" ? { reason: reason || undefined } : {});
      setSheet(null);
      settle(updated);
    } catch (err) {
      const fresh = err instanceof ApiError && err.status === 409 ? await get<ApprovalDTO>(`/api/approvals/${item.id}`).catch(() => null) : null;
      if (!fresh) throw err;
      setSheet(null);
      setNotice((err as Error).message);
      settle(fresh);
    } finally {
      setBusy(false);
    }
  };

  /** Taking an older version after all: the platform files a copy of it as the next version, approves it and sends it. */
  const sendVersion = async (id: string) => {
    if (!item) return;
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      decidedHere.current.add(item.id);
      const copy = await post<ApprovalDTO>(`/api/approvals/${id}/approve-version`, {});
      known.current.delete(item.id);
      setShowingId(null);
      settle(copy);
    } catch (err) {
      decidedHere.current.delete(item.id);
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const versions = context?.versions ?? [];
  const shown = showingId ? versions.find((v) => v.id === showingId) : null;
  const drawn = (shown?.payload ?? item?.payload ?? {}) as ReviewItem;
  const why = text(drawn, "why");

  return (
    <Overlay
      onClose={onClose}
      fit
      title={<span className="text-heading font-semibold tracking-tight">{template.page}</span>}
      trailing={
        user.role === "admin" ? (
          <Tabs
            label="Show"
            value={who}
            onChange={(value) => {
              if (value === who) return;
              landed.current = true;
              setNotice(null);
              setQueue(null);
              setWho(value);
            }}
            tabs={[{ key: "mine", label: "Mine" }, { key: "all", label: "All" }]}
          />
        ) : undefined
      }
    >
      {queue && queue.length > 0 && !done && (
        <div className="mx-auto w-full max-w-2xl shrink-0 px-4 pt-4 md:px-6">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted tabular-nums">
              {index + 1} of {queue.length}
            </span>
            <div className="h-1 min-w-0 flex-1 overflow-hidden rounded-full bg-fill-strong">
              <div className="h-full rounded-full bg-fg transition-[width] duration-300" style={{ width: `${((index + 1) / queue.length) * 100}%` }} />
            </div>
            <div className="flex gap-0.5">
              <IconButton label="Previous" fill="none" disabled={index === 0 || busy} onClick={() => advance(-1)}>
                <Icon name="chevron" className="size-4 rotate-180" />
              </IconButton>
              <IconButton label="Next" fill="none" disabled={index + 1 >= queue.length || busy} onClick={() => advance()}>
                <Icon name="chevron" className="size-4" />
              </IconButton>
            </div>
          </div>
        </div>
      )}

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-2xl px-4 pt-4 pb-6 md:px-6">
          <ErrorText error={error} />
          {notice && <p className="mb-3 rounded-xl bg-warning/10 px-3 py-2 text-sm text-warning">{notice}</p>}
          {!queue ? (
            <div className="card h-72 animate-pulse" />
          ) : done ? (
            <AllDone
              count={queue.length}
              more={more}
              onClose={onClose}
              onContinue={() => {
                setNotice(null);
                void load();
              }}
            />
          ) : (
            item && (
              <div key={item.id} className={exiting ? "animate-card-out" : "animate-card-in"}>
                <Parties
                  item={(item.payload ?? {}) as ReviewItem}
                  sales={context?.salesos ?? null}
                  trailing={
                    versions.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => setHistory(true)}
                        className="shrink-0 rounded-full bg-fill px-2 py-0.5 text-tiny font-medium text-muted hover:bg-fill-strong"
                        aria-label="Versions of this message"
                      >
                        v{shown?.version ?? item.version}
                      </button>
                    ) : item.version > 1 ? (
                      <Badge tone="zinc">v{item.version}</Badge>
                    ) : undefined
                  }
                />
                <div className="mt-3">
                  <StateStrip sales={context?.salesos ?? null} />
                </div>
                {shown && (
                  <p className="mt-4 rounded-xl bg-fill-subtle px-3 py-2 text-sm text-muted">
                    Version {shown.version}, filed {shortDate(shown.filedAt)} · {verdictWord(shown)}
                    {shown.decisionNote ? ` · “${shown.decisionNote}”` : ""}
                  </p>
                )}
                {why && (
                  <div className="mt-4">
                    <WhyNow why={why} />
                  </div>
                )}
                <div className="mt-4">
                  <MessageFrame item={drawn} />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-faint">
                  <AgentAvatar agent={item.agentId} name={item.agentName} size="sm" />
                  <span>
                    {item.agentName} filed this {timeAgo(item.createdAt)}
                  </span>
                  {item.conversationId && (
                    <TextLink to={`/c/${item.conversationId}`} quiet>
                      open the chat
                    </TextLink>
                  )}
                </div>
              </div>
            )
          )}
        </div>
      </div>

      {item && !done && (
        <div className="glass shrink-0 border-t border-line">
          <div className="mx-auto w-full max-w-2xl px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:px-6">
            {item.status !== "pending" ? (
              /* Approved, and the send did not settle: why, and what its addressee can do about it. */
              <ReleaseBar
                a={item}
                canAct={canSettleRelease(item, user)}
                canCheck={canCheckSend(item, user)}
                busy={busy}
                onSend={() => release("send").catch((err) => setError((err as Error).message))}
                onChecked={() => release("checked").catch((err) => setError((err as Error).message))}
                onWithdraw={() => setSheet("withdraw")}
                onNext={() => advance()}
              />
            ) : shown ? (
              /* An older version is read-only: the only verdict it takes is being sent after all, as a new version. */
              <div className="flex gap-2">
                <Button variant="primary" className={thumbCls} disabled={busy || !canDecide} onClick={() => sendVersion(shown.id)}>
                  Send this version
                </Button>
                <Button className={thumbCls} disabled={busy} onClick={() => setShowingId(null)}>
                  Back to the latest
                </Button>
              </div>
            ) : (
              /* The three verdicts in one row, each a thumb's reach: a revision is asked for as often as a yes. */
              <div className="flex gap-2">
                <Button variant="primary" className={thumbCls} disabled={busy || !canDecide} onClick={() => verdict("approve")}>
                  Approve
                </Button>
                <Button className={thumbCls} disabled={busy || !canDecide} onClick={() => setSheet("revise")}>
                  <span className="sm:hidden">Revise</span>
                  <span className="hidden sm:inline">Request revision</span>
                </Button>
                <Button className={thumbCls} disabled={busy || !canDecide} onClick={() => setSheet("reject")}>
                  Reject
                </Button>
              </div>
            )}
            {/* An admin paging through everyone's queue reads, and whoever it is addressed to decides. */}
            {item.status === "pending" &&
              (canDecide ? (
                <p className="mt-2 text-center text-tiny text-faint">{releaseText(template.release)}.</p>
              ) : (
                <p className="mt-2 text-center text-sm text-muted">Addressed to {waitingForText(item)}</p>
              ))}
          </div>
        </div>
      )}

      {history && <VersionHistory versions={versions} showing={showingId} onShow={setShowingId} onClose={() => setHistory(false)} />}
      {sheet && item && (
        <NoteSheet
          mode={sheet}
          about={item}
          onClose={() => setSheet(null)}
          onSend={(note) => (sheet === "withdraw" ? release("withdraw", note) : decide(sheet, note))}
        />
      )}
    </Overlay>
  );
}
